// src/theme-glass.ts
import { createTheme, alpha } from "@mui/material/styles";

const fontFamily = [
  "Inter",
  "system-ui",
  "-apple-system",
  "Segoe UI",
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
].join(",");

const glass = (bg: string, border: string) => ({
  backgroundColor: bg,
  border: `1px solid ${border}`,
  backdropFilter: "blur(14px) saturate(140%)",
  WebkitBackdropFilter: "blur(14px) saturate(140%)",
});

const common = {
  shape: { borderRadius: 16 },
  typography: {
    fontFamily,
    h6: { fontWeight: 800 },
    button: { textTransform: "none", fontWeight: 700 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Glass braucht einen "lebendigen" Hintergrund
          background:
            "radial-gradient(1200px 700px at 20% 10%, rgba(37,99,235,0.18), transparent 60%)," +
            "radial-gradient(900px 600px at 80% 20%, rgba(34,197,94,0.14), transparent 55%)," +
            "radial-gradient(900px 700px at 30% 85%, rgba(236,72,153,0.10), transparent 60%)," +
            "linear-gradient(180deg, #F6F7FB 0%, #EEF2FF 100%)",
          backgroundAttachment: "fixed",
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          ...glass(
            alpha(theme.palette.background.paper, 0.72),
            alpha(theme.palette.divider, 0.30)
          ),
          boxShadow: "0 12px 40px rgba(15, 23, 42, 0.10)",
          backgroundImage: "none",
        }),
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          ...glass(alpha(theme.palette.background.paper, 0.60), alpha(theme.palette.divider, 0.30)),
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
        }),
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }: any) => ({
          ...glass(alpha(theme.palette.background.paper, 0.60), alpha(theme.palette.divider, 0.30)),
        }),
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: 14,
          transition: "transform 120ms ease, background-color 120ms ease, box-shadow 120ms ease",
          "&:active": { transform: "scale(0.99)" },
        }),
        contained: ({ theme }: any) => ({
          boxShadow: "0 12px 30px rgba(37, 99, 235, 0.18)",
        }),
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          borderRadius: 14,
          backgroundColor: alpha(theme.palette.background.paper, 0.55),
          backdropFilter: "blur(10px) saturate(140%)",
          WebkitBackdropFilter: "blur(10px) saturate(140%)",
        }),
      },
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          ...glass(alpha(theme.palette.background.paper, 0.70), alpha(theme.palette.divider, 0.30)),
        }),
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
        },
      },
    },
  },
};

export const glassLightTheme = createTheme({
  ...common,
  palette: {
    mode: "light",
    background: {
      default: "#F6F7FB",
      paper: "#FFFFFF",
    },
    primary: { main: "#2563EB" },
    secondary: { main: "#22C55E" },
    text: { primary: "#0F172A", secondary: "#475569" },
    divider: alpha("#0F172A", 0.10),
  },
});

export const glassDarkTheme = createTheme({
  ...common,
  components: {
    ...common.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background:
            "radial-gradient(1200px 700px at 20% 10%, rgba(96,165,250,0.22), transparent 60%)," +
            "radial-gradient(900px 600px at 80% 20%, rgba(52,211,153,0.14), transparent 55%)," +
            "radial-gradient(900px 700px at 30% 85%, rgba(244,114,182,0.12), transparent 60%)," +
            "linear-gradient(180deg, #0B1020 0%, #070A13 100%)",
          backgroundAttachment: "fixed",
        },
      },
    },
  },
  palette: {
    mode: "dark",
    background: {
      default: "#0B1020",
      paper: "#111A2E",
    },
    primary: { main: "#60A5FA" },
    secondary: { main: "#34D399" },
    text: { primary: "#E5E7EB", secondary: "#9CA3AF" },
    divider: alpha("#FFFFFF", 0.12),
  },
});
